import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Modal, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Close } from '@mui/icons-material';
import QueryString from 'qs';
import { useToast } from 'use-toast-mui';
import moment from 'moment';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  searchTickets,
  getTicketById,
  getCoursePassSelectionOptions,
  getPassById,
  getTrialById,
} from '../../../../api/ticket';
import { getBranchManager } from '../../../../api/staff';
import { createProductList, createProductListGroup, createProductBulk, getRegisterTypeDefault } from '../../../../api/payment';
import { getHeadBranchId } from '../../../../api/point';
import { getPointTrainers } from '../../../../api/staff';
import { getMemberByBranchAndTemporary } from '../../../../api/member-management';
import { fetchTemporaryUserDetail, searchUserByPhoneNumber } from '../../../../api/temporaryUser';
import { errorMessages } from '../../../../utils/methods';
import {
  SearchModalContainer,
  SearchModalTitle,
  SearchModalSubtitle,
  SearchModalFooter,
  DupUserList,
  DupUserCard,
  DupUserCardHeader,
  DupUserName,
  DupUserMeta,
  DupStatusBadge,
  DupCardDivider,
  DupBranchChipRow,
  DupBranchChip,
  DupAssetRow,
  DupAssetBranch,
  DupAssetName,
  DupAssetCount,
  DupAssetDate,
  DupExpiredNote,
  DupEmptyNote,
} from '../../../temporaryUser/TemporaryUserStyled';

import EnterSaleInfoModalTicketSection from './EnterSaleInfoModalTicketSection';
import EnterSaleInfoModalPassSection from './EnterSaleInfoModalPassSection';
import EnterSaleInfoModalProductSection from './EnterSaleInfoModalProductSection';
import EnterSaleInfoModalPaymentSection from './EnterSaleInfoModalPaymentSection';
import EnterSaleInfoModalCartSection from './EnterSaleInfoModalCartSection';
import { REGISTER_TYPE } from './RegisterTypeToggle';
import PaymentConfirmModal from './PaymentConfirmModal';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import { SPLIT_METHOD_LABEL } from './SplitPaymentSection';
import usePointUsage from './usePointUsage';
import { buildSellerPayload, isSellerShareValid, sellerDisplayName } from '../../../common/SellerShareSelect';

import {
  StyledModal,
  ModalContainer,
  ModalContainerWithCart,
  ModalMainSection,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconWrapper,
  ModalContent,
  ModalFooter,
  TabContainer,
  TabButton,
  OpenButton,
  CancelButton,
  AddToCartButton,
  SaveButton,
} from './EnterSaleInfoModalStyled';

// ============================================
// Constants
// ============================================
const DEFAULT_FORM_VALUES = {
  searchInput: '',
  paymentMethod: null,
  registerType: REGISTER_TYPE.NEW, // #124 신규등록/재등록 — 열릴 때 서버 자동 판정값으로 덮어쓴다
  branchManager: [], // #122 판매 담당자 user ID 배열(선택 순서 보존)
  sellerShares: {}, // #122 { [userId]: 귀속 비율 % } — 2명 이상일 때만 채워진다
  memo: '',
  coursePass: null,
  courseStartDate: '',
  courseEndDate: '',
  saleDate: '',
  cardCompanyCode: null,
  cardNumber: '',
  approvalNumber: '',
  receiptYn: 'Y',
  deductionType: 0,
  receiptType: 'phone',
  personalId: '',
  businessNumber: '',
  bankInfo: '',
  depositorName: '',
  trainers: [],
  usedPoint: '',
};

// ============================================
// 중복체크 모달 표시 헬퍼
// ============================================

const DUP_STATUS_LABEL = {
  VALID: '유효',
  EXPIRED: '만료',
  NONE: '이력 없음',
};

const dupStatusLabel = status => DUP_STATUS_LABEL[status] || DUP_STATUS_LABEL.NONE;

// 무제한 수강권은 횟수가 없으므로 빈칸으로 둔다
const formatTicketCount = ticket => {
  if (ticket.usageCount === null || ticket.usageCount === undefined) return '';
  const remain = ticket.usageCount - (ticket.usedCount ?? 0);
  return `${remain}/${ticket.usageCount}`;
};

// 2026-10-08 → ~26.10.08 (좁은 칸에 들어가야 해서 연도 2자리)
const formatUntil = expireDate => {
  if (!expireDate) return '';
  const [year, month, day] = String(expireDate).slice(0, 10).split('-');
  return `~${year.slice(2)}.${month}.${day}`;
};

const formatExpiredSummary = (summary, t) => {
  const parts = [];
  if (summary.ticketCount > 0) parts.push(`${t('만료 수강권')} ${summary.ticketCount}${t('건')}`);
  if (summary.passCount > 0) parts.push(`${t('만료 이용권')} ${summary.passCount}${t('건')}`);
  return parts.join(' · ');
};

const EnterSaleInfoModal = ({
  userInfo,
  userDetail,
  fetchData,
  open: externalOpen,
  onOpen,
  onClose,
  hideButton = false,
  // 회원상세 라커 카드 [연장] 진입 — 라커 탭으로 열고 해당 자리·기간을 프리셋한다.
  initialLockerExtension = null,
  // 잠재회원 등록/상담에서 바로 넘어온 판매등록 — 사용자가 닫으려 할 때 한 번 확인한다.
  closeConfirmMessage = '',
  // 잠재회원 판매 성공 후 /temporary 하드 이동 여부. 신규 진입 경로에서는 현재 화면을 지킨다.
  redirectOnTemporarySuccess = true,
  // 방금 같은 번호 검사를 통과해 만든 잠재회원이면 저장 직전 중복체크를 다시 하지 않는다.
  skipTemporaryDupCheck = false,
  // 실제로 저장이 일어났을 때만 호출된다 (취소로 닫은 경우와 구분).
  onSaved,
  // [판매 등록] 버튼 색을 화면에 맞출 때(잠재회원 화면은 하늘색)
  openButtonSx,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useSelector(state => state.user);

  // ============================================
  // Form
  // ============================================
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
    setError,
    reset,
    watch,
  } = useForm({ defaultValues: DEFAULT_FORM_VALUES });

  // ============================================
  // State - Modal
  // ============================================
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);


  // ============================================
  // State - Data
  // ============================================
  const [selectedBranch, setSelectedBranch] = useState(user.branch?.branchId || null);

  // ============================================
  // State - Tab Selection (ticket, pass, product)
  // ============================================
  const [activeTab, setActiveTab] = useState('ticket');
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [productType, setProductType] = useState('STOCK'); // 'inventory' (재고상품) or 'period' (기간상품)

  // [연장] 진입이면 열릴 때 일반상품(라커) 탭으로 전환. 자리·기간 프리셋은 PaymentSection 이 처리.
  useEffect(() => {
    if (open && initialLockerExtension) {
      setActiveTab('product');
      setProductType('LOCKER');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialLockerExtension]);
  const [showExpiredProducts, setShowExpiredProducts] = useState(false); // 판매종료 상품 노출 여부

  // ============================================
  // State - Ticket Pricing
  // ============================================
  const [usageCount, setUsageCount] = useState([]);
  const [ticketPrice, setTicketPrice] = useState([]);
  // 상품 마스터 정가. 판매금액을 현장에서 수정할 수 있으므로, 원가 초과 확인 모달의 비교 기준으로만 쓴다.
  const [ticketBasePrice, setTicketBasePrice] = useState(0);
  const [ticketDiscountAmount, setTicketDiscountAmount] = useState('');
  const [ticketFinalPrice, setTicketFinalPrice] = useState('');
  const [ticketOutstandingPrice, setTicketOutstandingPrice] = useState('');
  const [ticketUsagePeriod, setTicketUsagePeriod] = useState(null);
  const [ticketUsagePeriodType, setTicketUsagePeriodType] = useState(null);

  // ============================================
  // State - Pass Pricing
  // ============================================
  const [ticketPassPrice, setTicketPassPrice] = useState(null);
  const [passBasePrice, setPassBasePrice] = useState(0);
  const [ticketPassDiscountAmount, setTicketPassDiscountAmount] = useState('');
  const [ticketPassFinalPrice, setTicketPassFinalPrice] = useState('');
  const [ticketPassOutstandingPrice, setTicketPassOutstandingPrice] = useState(null);
  const [passUsagePeriod, setPassUsagePeriod] = useState(null);
  const [passUsagePeriodType, setPassUsagePeriodType] = useState(null);

  // ============================================
  // State - Product Pricing
  // ============================================
  const [productList, setProductList] = useState([]);
  const [productPrice, setProductPrice] = useState(0);
  const [productBasePrice, setProductBasePrice] = useState(0);
  const [productDiscountAmount, setProductDiscountAmount] = useState('');
  const [productFinalPrice, setProductFinalPrice] = useState('');
  const [productOutstandingPrice, setProductOutstandingPrice] = useState('');
  const [productQuantity, setProductQuantity] = useState('');

  // ============================================
  // State - Duet Lesson
  // ============================================
  const [memberId, setMemberId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [splitPayment, setSplitPayment] = useState(false);
  // 듀엣 나눠결제 미수금 — 결제금액과 마찬가지로 회원별로 직접 입력받는다
  const [splitOutstandingAmounts, setSplitOutstandingAmounts] = useState({
    userDetail: '',
    selectedMember: '',
  });
  const [splitPaymentAmounts, setSplitPaymentAmounts] = useState({
    userDetail: '',
    selectedMember: '',
  });
  // 결제 수납 라인(#49). 결제 모달에서 수단별로 입력받는다.
  // 위 splitPayment(듀엣레슨 회원 간 금액 분할)와는 다른 개념이다.
  const [mixedPayLines, setMixedPayLines] = useState([]);

  // ============================================
  // State - Error Fields
  // ============================================
  const [errorFields, setErrorFields] = useState({});

  // ============================================
  // State - Cart Items (담기)
  // ============================================
  const [cartItems, setCartItems] = useState([]);

  // ============================================
  // State - Payment Modal (결제 정보 모달)
  // ============================================
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  // 상품 원가보다 비싸게 파는 건은 저장 직전 한 번 더 확인받는다(오타 방지). null 이면 확인 불필요.
  const [overPriceItem, setOverPriceItem] = useState(null);

  // ============================================
  // State - 중복체크 (저장 직전 휴대폰 번호로 기존 회원 검색)
  // ============================================
  const [dupCheckModalOpen, setDupCheckModalOpen] = useState(false);
  const [dupCheckResults, setDupCheckResults] = useState([]);

  // ============================================
  // React Query - Data Fetching
  // ============================================

  // 지점 목록 조회
  const { data: branches = [] } = useQuery({
    queryKey: ['branches', user.headBranch?.headBranchId],
    queryFn: () => getHeadBranchId(user.token, user.headBranch?.headBranchId),
    enabled: !!user.headBranch?.headBranchId && !!user.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // #124 신규/재등록 기본값 — 회원이면 서버 자동 판정(그 지점 첫 결제 여부), 잠재회원은 신규
  useEffect(() => {
    if (!open || !userInfo?.userId || !selectedBranch || !user?.token) return;
    if (userInfo.userType !== 'member') {
      setValue('registerType', REGISTER_TYPE.NEW);
      return;
    }
    let cancelled = false;
    getRegisterTypeDefault(userInfo.userId, selectedBranch, user.token)
      .then(data => {
        if (!cancelled && data?.registerType) setValue('registerType', data.registerType);
      })
      .catch(() => {
        /* 기본값 조회 실패는 치명적이지 않다 — 신규로 두고 사용자가 고른다 */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userInfo?.userId, userInfo?.userType, selectedBranch]);

  // 지점 담당자 목록 조회
  const { data: branchManager = null } = useQuery({
    queryKey: ['branchManager', selectedBranch],
    queryFn: async () => {
      const data = await getBranchManager(selectedBranch, user.token);
      return data.map(item => ({
        value: item.managerId,
        label: item.managerName,
      }));
    },
    enabled: !!selectedBranch && !!user.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 강사 목록 조회
  // 담당강사 배정은 지점별이고 판매 건의 배정 지점은 '판매지점(selectedBranch)'이다.
  // 후보를 로그인 지점으로 뽑으면 지점을 바꿔 판매할 때 타 지점 강사가 배정된다.
  const { data: trainers = null } = useQuery({
    queryKey: ['trainers-options', selectedBranch],
    queryFn: async () => {
      const param = selectedBranch + '&courseClassType=PRIVATE_LESSON';
      const data = await getPointTrainers(param, user.token);
      return [
        { value: '', label: '담당 강사를 선택해주세요' },
        ...data.map(item => ({
          value: item.trainerId,
          label: item.trainerName,
        })),
      ];
    },
    enabled: !!selectedBranch && !!user.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 수강권 목록 조회
  const { data: courseTicket = [] } = useQuery({
    queryKey: ['courseTicket', selectedBranch],
    queryFn: async () => {
      const data = await searchTickets(user.token, selectedBranch);
      return data.map(item => {
        const sub = [(item.usageCount ? item.usageCount + '회' : ''), item?.usagePeriodDisplay].filter(Boolean).join(' ');
        return {
          value: item.courseTicketId,
          label: item.courseTicketName + (sub ? ' ' + sub : ''),
          name: item.courseTicketName,
          sub,
          status: item.saleStatus || item.salesStatus || item.approvalStatus || 'ON_SALE',
          ticketType: item.ticketType,
          classType: item.classType,
          sportType: item.sportType,
          sportTypes: item.sportTypes || (item.sportType ? [item.sportType] : []),
          regularPrice: item.regularPrice,
          finalPrice: item.finalPrice,
          favorite: !!item.favorite, // 지점 공용 즐겨찾기 — 목록 최상단 '즐겨찾기' 그룹
        };
      });
    },
    enabled: !!selectedBranch && !!user.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 이용권(패스 + PASS 체험권) 셀렉트 옵션 조회 (슬림 API).
  // 목록에 표시할 정가/판매가만 응답에 있고, 폼에 채울 금액은 옵션 선택 후 sourceType 분기로 상세 API 에서 가져온다.
  const { data: coursePass = null } = useQuery({
    queryKey: ['coursePass', user.branch?.branchId],
    queryFn: async () => {
      const data = await getCoursePassSelectionOptions(user.token, user.branch?.branchId);
      return (data || []).map(item => ({
        value: item.coursePassId,
        label: item.coursePassName + ' ' + item?.usagePeriodDisplay,
        status: item.saleStatus || 'ON_SALE',
        usagePeriod: item.usagePeriod,
        usagePeriodType: item.usagePeriodType,
        sourceType: item.sourceType, // 'COURSE_PASS' | 'TRIAL_TICKET' — 가격 fetch 분기 키
        subscriptionYn: item.subscriptionYn, // 구독제 이용권 여부 — 선택 목록 "구독" 뱃지용
        classType: item.classType,
        sportType: item.sportType,
        sportTypes: item.sportTypes || (item.sportType ? [item.sportType] : []),
        regularPrice: item.regularPrice,
        finalPrice: item.finalPrice,
        favorite: !!item.favorite, // 지점 공용 즐겨찾기 — 목록 최상단 '즐겨찾기' 그룹
      }));
    },
    enabled: !!user.branch?.branchId && !!user.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // ============================================
  // Point Usage (보유 포인트 조회 + 사용 가능 포인트 계산 + 유효성 검사)
  // ============================================
  const pointUsage = usePointUsage({
    memberId: userInfo?.userId,
    userType: userInfo?.userType,
    branchId: selectedBranch,
    token: user.token,
    cartItems,
  });

  // ============================================
  // Utility Functions
  // ============================================
  const formatInteger = value => {
    if (!value && value !== 0) return '0';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // ============================================
  // Cart Summary (결제 모달에 전달)
  // ============================================
  const cartSummary = useMemo(() => {
    if (!cartItems || cartItems.length === 0) {
      return {
        totalCount: 0,
        totalPrice: 0,
        totalDiscount: 0,
        totalOutstanding: 0,
        totalFinalPrice: 0,
        totalUsedPoint: 0,
      };
    }

    return cartItems.reduce(
      (acc, item) => ({
        totalCount: acc.totalCount + 1,
        totalPrice: acc.totalPrice + (Number(String(item.price || 0).replace(/,/g, '')) || 0),
        totalDiscount: acc.totalDiscount + (Number(String(item.discountAmount || 0).replace(/,/g, '')) || 0),
        totalOutstanding: acc.totalOutstanding + (Number(String(item.outstandingPrice || 0).replace(/,/g, '')) || 0),
        totalFinalPrice: acc.totalFinalPrice + (Number(String(item.finalPrice || 0).replace(/,/g, '')) || 0),
        totalUsedPoint: acc.totalUsedPoint + (Number(item.usedPoint) || 0),
      }),
      {
        totalCount: 0,
        totalPrice: 0,
        totalDiscount: 0,
        totalOutstanding: 0,
        totalFinalPrice: 0,
        totalUsedPoint: 0,
      },
    );
  }, [cartItems]);

  // ============================================
  // Modal Handlers
  // ============================================
  const handleOpen = () => {
    if (!user.branch?.branchId) {
      toast.error('지점 정보가 없습니다');
      return;
    }
    if (onOpen) {
      onOpen(true);
    } else {
      setInternalOpen(true);
    }
  };

  const handleClose = (e, r) => {
    if (r === 'backdropClick') return;

    // Reset all state
    setErrorFields({});
    setActiveTab('ticket');
    setProductType('inventory');
    setShowExpiredProducts(false);
    setUsageCount(0);
    setTicketPrice([]);
    setTicketBasePrice(0);
    setTicketDiscountAmount('');
    setTicketFinalPrice('');
    setTicketOutstandingPrice('');
    setTicketUsagePeriod(null);
    setTicketUsagePeriodType(null);
    setTicketPassPrice([]);
    setPassBasePrice(0);
    setTicketPassDiscountAmount('');
    setTicketPassFinalPrice('');
    setTicketPassOutstandingPrice('');
    setPassUsagePeriod(null);
    setPassUsagePeriodType(null);
    setProductPrice(0);
    setProductBasePrice(0);
    setProductDiscountAmount('');
    setProductFinalPrice('');
    setProductOutstandingPrice('');
    setProductQuantity('');
    setSelectedTicketType(null);
    setSplitPayment(false);
    setSplitPaymentAmounts({ userDetail: '', selectedMember: '' });
    setSplitOutstandingAmounts({ userDetail: '', selectedMember: '' });
    setMixedPayLines([]);
    setSelectedMembers([]);
    setCartItems([]);
    setPaymentModalOpen(false);
    setOverPriceItem(null);
    setDupCheckModalOpen(false);
    setDupCheckResults([]);

    reset({
      paymentMethod: null,
      registerType: REGISTER_TYPE.NEW,
      branchManager: [],
      sellerShares: {},
      memo: '',
      coursePass: null,
      courseStartDate: '',
      courseEndDate: '',
      saleDate: '',
      trainers: [],
      splitPayment_userDetail: '',
      splitPayment_selectedMember_0: '',
      splitOutstanding_userDetail: '',
      splitOutstanding_selectedMember_0: '',
      usedPoint: '',
    });

    if (onClose) {
      onClose();
    } else if (onOpen) {
      onOpen(false);
    } else {
      setInternalOpen(false);
    }
  };

  // 사용자가 직접 닫으려는 경로에서만 확인을 받는다. 저장 성공 후 닫기는 handleClose 를 직접 부른다.
  const requestClose = (e, r) => {
    if (r === 'backdropClick') return;
    if (closeConfirmMessage) {
      setCloseConfirmOpen(true);
      return;
    }
    handleClose(e, r);
  };

  // ============================================
  // API Calls
  // ============================================
  const getPrice = async id => {
    try {
      // 선택된 티켓이 일일권인지 확인
      const selectedTicket = courseTicket.find(ticket => ticket.value === id);
      const isTrialTicket = selectedTicket?.ticketType === 'TRIAL_TICKET';

      // 일일권이면 trial API, 아니면 일반 ticket API 호출
      const data = isTrialTicket ? await getTrialById(id, user.token) : await getTicketById(id, user.token);

      const discountAmount = data.regularPrice - data.finalPrice;
      setUsageCount(data.usageCount);
      setTicketPrice(data.regularPrice);
      setTicketBasePrice(data.regularPrice || 0);
      setTicketDiscountAmount(discountAmount);
      setTicketFinalPrice(data.finalPrice);
      setSelectedTicketType(data.classType);
      // 기간 정보 저장
      setTicketUsagePeriod(data.usagePeriod);
      setTicketUsagePeriodType(data.usagePeriodType);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const getPassPrice = async id => {
    const selectedPass = coursePass?.find(pass => pass.value === id);
    if (!selectedPass) {
      setTicketPassPrice(null);
      setPassUsagePeriod(null);
      setPassUsagePeriodType(null);
      return;
    }
    try {
      // 슬림 API 응답은 목록 표시용 금액만 담고 있어, 폼 값은 sourceType 으로 상세 API 분기해서 받아온다.
      const data =
        selectedPass.sourceType === 'TRIAL_TICKET'
          ? await getTrialById(id, user.token)
          : await getPassById(id, user.token);
      const discountAmount = (data.regularPrice || 0) - (data.finalPrice || 0);
      setTicketPassPrice(data.regularPrice);
      setPassBasePrice(data.regularPrice || 0);
      setTicketPassDiscountAmount(discountAmount);
      setTicketPassFinalPrice(data.finalPrice);
      setPassUsagePeriod(data.usagePeriod);
      setPassUsagePeriodType(data.usagePeriodType);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const getMembers = async searchValue => {
    if (!searchValue || searchValue.trim().length === 0) {
      setSearchResults([]);
      return;
    }
    try {
      const qstring = QueryString.stringify({
        searchInput: searchValue,
        headBranchId: user.headBranch?.headBranchId,
        branchId: user.branch?.branchId,
      });
      const data = await getMemberByBranchAndTemporary(user.token, qstring);
      const transformArray = data?.content?.map(item => ({
        id: item.memberId,
        name: item.name + ' (' + item.phoneNumber + ')',
        userType: item.userType,
      }));
      setSearchResults(transformArray);
    } catch (error) {
      console.error('회원 검색 중 오류 발생:', error);
    }
  };

  const debouncedGetMembers = debounce(getMembers, 300);

  const handleChangeMember = async selected => {
    if (selected && selected !== -1) {
      const member = searchResults.find(item => item.id === selected);
      if (!member) return;
      setMemberId(member.id);
    }
  };

  // ============================================
  // Price Calculation Handlers
  // ============================================
  const toIntValue = value => parseInt(String(value ?? 0).replace(/[^\d]/g, ''), 10) || 0;

  // 판매금액 직접 편집. 현장에서 상품 정가와 다른 금액(정가 초과 포함)으로 파는 경우를 지원한다.
  // 할인·미수금은 입력된 값을 그대로 두고 결제금액만 다시 계산한다 — 판매금액을 올렸다고
  // 차액이 할인으로 잡히면 하지도 않은 할인이 매출에 기록된다.
  // 할인·미수금은 입력된 값을 그대로 두고 결제금액만 다시 계산한다 — 판매금액을 올렸다고
  // 차액이 할인으로 잡히면 하지도 않은 할인이 매출에 기록된다.
  // 여기서 할인을 자동으로 깎으면 안 된다. 한 글자씩 입력되는 동안 중간값("3")으로 할인이
  // 뭉개져 복구되지 않는다. 결제금액이 음수가 되는 조합은 저장 단계(validateForm)에서 막는다.
  const changeTicketPrice = value => {
    const price = toIntValue(value);
    setTicketPrice(price);
    setTicketFinalPrice(price - (toIntValue(ticketDiscountAmount) + toIntValue(ticketOutstandingPrice)));
  };

  const changeTicketPassPrice = value => {
    const price = toIntValue(value);
    setTicketPassPrice(price);
    setTicketPassFinalPrice(
      price - (toIntValue(ticketPassDiscountAmount) + toIntValue(ticketPassOutstandingPrice)),
    );
  };

  // 일반상품의 판매금액 필드는 단가다(결제금액은 단가×수량 기준). 단가를 고치면 수량은 그대로 유지된다.
  const changeProductPrice = value => {
    const price = toIntValue(value);
    setProductPrice(price);
    setProductFinalPrice(
      calcProductTotalPrice(price, productQuantity) -
        (toIntValue(productDiscountAmount) + toIntValue(productOutstandingPrice)),
    );
  };

  const changeTicketFinalPrice = value => {
    value = parseInt(value.replace(/[^\d]/g, '')) || 0;
    const outPrice = parseInt(String(ticketOutstandingPrice || 0).replace(/[^\d]/g, '')) || 0;
    const changeValue = ticketPrice - (value + outPrice);
    setTicketDiscountAmount(value);
    setTicketFinalPrice(changeValue);
  };

  const changeTicketOutstandingPrice = value => {
    value = parseInt(value.replace(/[^\d]/g, '')) || 0;
    const disPrice = parseInt(String(ticketDiscountAmount || 0).replace(/[^\d]/g, '')) || 0;
    const changeValue = ticketPrice - (value + disPrice);
    setTicketOutstandingPrice(value);
    setTicketFinalPrice(changeValue);
  };

  // 결제금액을 직접 입력하면 할인금액을 역산한다(정가 30,000 → 결제 20,000 입력 시 할인 10,000).
  // 미수금은 이미 입력된 값을 유지하고, 남는 차액을 전부 할인으로 본다.
  // 할인이 음수가 되는 입력(결제+미수금 > 정가)은 0으로 클램프해 정가를 초과하지 않게 한다.
  const changeTicketPaidPrice = value => {
    value = parseInt(String(value).replace(/[^\d]/g, '')) || 0;
    const outPrice = parseInt(String(ticketOutstandingPrice || 0).replace(/[^\d]/g, '')) || 0;
    // 입력값은 포인트 차감 후 표시 금액이므로, 할인 역산 시 사용 포인트를 더해 차감 전 기준으로 계산한다.
    const usedPoint = resolveUsedPoint(watchedUsedPoint);
    const discount = Math.max(0, ticketPrice - (value + outPrice + usedPoint));
    setTicketDiscountAmount(discount);
    setTicketFinalPrice(ticketPrice - (discount + outPrice));
  };

  const changeTicketPassFinalPrice = value => {
    value = parseInt(value.replace(/[^\d]/g, '')) || 0;
    const outPrice = parseInt(String(ticketPassOutstandingPrice || 0).replace(/[^\d]/g, '')) || 0;
    const changeValue = ticketPassPrice - (value + outPrice);
    setTicketPassDiscountAmount(value);
    setTicketPassFinalPrice(changeValue);
  };

  const changeTicketPassOutstandingPrice = value => {
    value = parseInt(value.replace(/[^\d]/g, '')) || 0;
    const disPrice = parseInt(String(ticketPassDiscountAmount || 0).replace(/[^\d]/g, '')) || 0;
    const changeValue = ticketPassPrice - (value + disPrice);
    setTicketPassOutstandingPrice(value);
    setTicketPassFinalPrice(changeValue);
  };

  // 결제금액 직접 입력 → 할인금액 역산 (수강권과 동일 규칙).
  const changeTicketPassPaidPrice = value => {
    value = parseInt(String(value).replace(/[^\d]/g, '')) || 0;
    const outPrice = parseInt(String(ticketPassOutstandingPrice || 0).replace(/[^\d]/g, '')) || 0;
    // 입력값은 포인트 차감 후 표시 금액 — 할인 역산 시 사용 포인트를 더해 차감 전 기준으로 계산.
    const usedPoint = resolveUsedPoint(watchedUsedPoint);
    const discount = Math.max(0, ticketPassPrice - (value + outPrice + usedPoint));
    setTicketPassDiscountAmount(discount);
    setTicketPassFinalPrice(ticketPassPrice - (discount + outPrice));
  };

  // ============================================
  // Product Price Calculation Handlers
  // ============================================
  const calcProductTotalPrice = (price, quantity) => price * (quantity || 1);

  const getProductPrice = (id, price) => {
    setProductPrice(price || 0);
    setProductBasePrice(price || 0);
    setProductFinalPrice(calcProductTotalPrice(price || 0, productQuantity));
    setProductDiscountAmount('');
    setProductOutstandingPrice('');
  };

  const resetProductFields = () => {
    setValue('productId', null);
    setProductPrice(0);
    setProductFinalPrice('');
    setProductDiscountAmount('');
    setProductOutstandingPrice('');
    setProductQuantity('');
  };

  const changeProductFinalPrice = value => {
    value = parseInt(value.replace(/[^\d]/g, '')) || 0;
    const outPrice = parseInt(String(productOutstandingPrice || 0).replace(/[^\d]/g, '')) || 0;
    const totalPrice = calcProductTotalPrice(productPrice, productQuantity);
    setProductDiscountAmount(value);
    setProductFinalPrice(totalPrice - (value + outPrice));
  };

  const changeProductOutstandingPrice = value => {
    value = parseInt(value.replace(/[^\d]/g, '')) || 0;
    const disPrice = parseInt(String(productDiscountAmount || 0).replace(/[^\d]/g, '')) || 0;
    const totalPrice = calcProductTotalPrice(productPrice, productQuantity);
    setProductOutstandingPrice(value);
    setProductFinalPrice(totalPrice - (value + disPrice));
  };

  // 결제금액 직접 입력 → 할인금액 역산. 일반상품은 정가가 단가×수량이라 그 합계를 기준으로 잡는다.
  const changeProductPaidPrice = value => {
    value = parseInt(String(value).replace(/[^\d]/g, '')) || 0;
    const outPrice = parseInt(String(productOutstandingPrice || 0).replace(/[^\d]/g, '')) || 0;
    const totalPrice = calcProductTotalPrice(productPrice, productQuantity);
    // 입력값은 포인트 차감 후 표시 금액 — 할인 역산 시 사용 포인트를 더해 차감 전 기준으로 계산.
    const usedPoint = resolveUsedPoint(watchedUsedPoint);
    const discount = Math.max(0, totalPrice - (value + outPrice + usedPoint));
    setProductDiscountAmount(discount);
    setProductFinalPrice(totalPrice - (discount + outPrice));
  };

  const changeProductQuantity = value => {
    const numValue = parseInt(value.replace(/[^\d]/g, '')) || '';
    const disPrice = parseInt(String(productDiscountAmount || 0).replace(/[^\d]/g, '')) || 0;
    const outPrice = parseInt(String(productOutstandingPrice || 0).replace(/[^\d]/g, '')) || 0;
    const totalPrice = calcProductTotalPrice(productPrice, numValue);
    setProductQuantity(numValue);
    setProductFinalPrice(totalPrice - (disPrice + outPrice));
  };

  // ============================================
  // Tab Change Handler
  // ============================================
  const handleTabChange = tab => {
    setActiveTab(tab);
    // 일반상품(라커) 탭 클릭 시 기본적으로 라커 선택
    if (tab === 'product') {
      setProductType('LOCKER');
    }
    // usedPoint는 3개 탭이 공유하는 필드 — 미확정 입력값이 다른 탭 결제로 넘어가지 않게 초기화
    setValue('usedPoint', '');
    setErrorFields(prev => ({ ...prev, usedPoint: false }));
  };

  // 포인트 필드 노출 조건 (비회원/설정 OFF/듀엣레슨 제외).
  // 미노출 상태에서 필드에 잔존한 입력값은 카트/단건 결제 모든 경로에서 0으로 무시해야 한다.
  const isPointFieldVisibleNow = () =>
    pointUsage.isPointUsageEnabled && !(activeTab === 'ticket' && selectedTicketType === 'DUET_LESSON');
  const resolveUsedPoint = value => (isPointFieldVisibleNow() ? Number(value) || 0 : 0);

  // 포인트 입력 시 결제금액 표시값을 실시간으로 차감하기 위한 감시값.
  // 상태값(ticketFinalPrice 등)은 포인트 차감 전 금액(판매금액-할인-미수금)을 유지하고,
  // 화면의 결제금액 필드에만 (상태값 - 사용 포인트)를 보여준다.
  const watchedUsedPoint = watch('usedPoint');

  // 힌트의 '최대'는 지점(센터) 설정 한도만 보여준다 — 보유·결제금액과 섞으면 보유 잔액이
  // 표시되어 혼란스럽다(사용자 결정). 실제 사용 가능액 검증은 제출 시 그대로 수행한다.
  const usablePointLimitFor = amount => {
    if (!isPointFieldVisibleNow()) return null;
    const base = parseInt(String(amount || 0).replace(/[^\d]/g, '')) || 0;
    const limit = pointUsage.resolveMaxUsable(base);
    return Number.isFinite(limit) && limit > 0 ? limit : null;
  };
  const displayFinalPrice = amount => {
    const used = resolveUsedPoint(watchedUsedPoint);
    if (!used) return amount;
    const base = parseInt(String(amount || 0).replace(/[^\d]/g, '')) || 0;
    return Math.max(0, base - used);
  };

  // 결제 모달 요약. 카트가 비어 있으면(단건 판매) 지금 입력 중인 상품 1건으로 만든다.
  const num = value => Number(String(value ?? 0).replace(/,/g, '')) || 0;
  const singleSummary = useMemo(() => {
    const price =
      activeTab === 'ticket'
        ? num(ticketPrice)
        : activeTab === 'pass'
          ? num(ticketPassPrice)
          : calcProductTotalPrice(num(productPrice), num(productQuantity));
    const discount =
      activeTab === 'ticket'
        ? num(ticketDiscountAmount)
        : activeTab === 'pass'
          ? num(ticketPassDiscountAmount)
          : num(productDiscountAmount);
    const outstanding =
      activeTab === 'ticket'
        ? num(ticketOutstandingPrice)
        : activeTab === 'pass'
          ? num(ticketPassOutstandingPrice)
          : num(productOutstandingPrice);
    const finalPrice =
      activeTab === 'ticket'
        ? num(ticketFinalPrice)
        : activeTab === 'pass'
          ? num(ticketPassFinalPrice)
          : num(productFinalPrice);
    return {
      totalCount: 1,
      totalPrice: price,
      totalDiscount: discount,
      totalOutstanding: outstanding,
      totalFinalPrice: finalPrice,
      totalUsedPoint: isPointFieldVisibleNow() ? num(watchedUsedPoint) : 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    ticketPrice,
    ticketPassPrice,
    productPrice,
    productQuantity,
    ticketDiscountAmount,
    ticketPassDiscountAmount,
    productDiscountAmount,
    ticketOutstandingPrice,
    ticketPassOutstandingPrice,
    productOutstandingPrice,
    ticketFinalPrice,
    ticketPassFinalPrice,
    productFinalPrice,
    watchedUsedPoint,
  ]);

  // ============================================
  // Cart Handlers (담기 기능)
  // ============================================
  const handleAddToCart = () => {
    const values = getValues();

    // 기본 유효성 검사 (상품 선택 여부)
    if (activeTab === 'ticket' && !values.courseTicket) {
      toast.error(t('수강권을 선택해주세요'));
      return;
    }
    if (activeTab === 'pass' && !values.coursePass) {
      toast.error(t('이용권을 선택해주세요'));
      return;
    }
    if (activeTab === 'product' && !values.productId) {
      toast.error(t('상품을 선택해주세요'));
      return;
    }

    // 라커 상품일 때 추가 유효성 검사 — 라커 선택은 옵션(미선택이면 미배정으로 판매되고
    // 배정은 회원상세에서 한다). 이용 기간만 필수.
    if (activeTab === 'product' && productType === 'LOCKER') {
      if (!values.courseStartDate) {
        toast.error(t('이용 시작일을 선택해주세요'));
        setErrorFields(prev => ({ ...prev, courseStartDate: true }));
        return;
      }
      if (!values.courseEndDate) {
        toast.error(t('이용 종료일을 선택해주세요'));
        setErrorFields(prev => ({ ...prev, courseEndDate: true }));
        return;
      }
    }

    // 판매 담당자 유효성 검사
    if (!values.branchManager?.length) {
      toast.error(t('판매 담당자를 선택해주세요'));
      setErrorFields(prev => ({ ...prev, branchManager: true }));
      return;
    }
    if (!isSellerShareValid(values.branchManager, values.sellerShares)) {
      toast.error(t('판매 담당자 귀속 비율의 합은 100% 여야 합니다'));
      setErrorFields(prev => ({ ...prev, branchManager: true }));
      return;
    }

    // 포인트 사용 유효성 검사 (듀엣레슨은 그룹결제라 이번 범위에서 포인트 사용 제외)
    const isPointFieldVisible = isPointFieldVisibleNow();
    if (isPointFieldVisible) {
      const activePaymentAmount =
        activeTab === 'ticket' ? ticketFinalPrice : activeTab === 'pass' ? ticketPassFinalPrice : productFinalPrice;
      const usedPointError = pointUsage.validateUsedPoint(values.usedPoint, activePaymentAmount);
      if (usedPointError) {
        toast.error(t(usedPointError));
        setErrorFields(prev => ({ ...prev, usedPoint: true }));
        return;
      }
    }

    // 판매 담당자 정보 가져오기 (#122 N명 + 비율)
    const sellerPayload = buildSellerPayload(values.branchManager, values.sellerShares);

    // 담을 상품 정보 구성
    let cartItem = {
      id: Date.now(), // 고유 ID
      type: activeTab,
      startDate: values.courseStartDate,
      endDate: values.courseEndDate,
      branchManagerId: sellerPayload.registeredBy || null,
      branchManagerIds: sellerPayload.registeredByIds || null,
      branchManagerRates: sellerPayload.shareRates || null,
      branchManagerName: sellerDisplayName(values.branchManager, values.sellerShares, branchManager),
    };

    if (activeTab === 'ticket') {
      const selectedTicket = courseTicket.find(t => t.value === values.courseTicket);
      const isTrialTicket = selectedTicket?.ticketType === 'TRIAL_TICKET';
      cartItem = {
        ...cartItem,
        productId: values.courseTicket,
        name: selectedTicket?.label || '',
        classType: selectedTicketType,
        price: ticketPrice,
        basePrice: ticketBasePrice,
        discountAmount: ticketDiscountAmount,
        outstandingPrice: ticketOutstandingPrice,
        finalPrice: ticketFinalPrice,
        usageCount: usageCount,
        trainerId: Array.isArray(values.trainers) && values.trainers.length ? values.trainers[0] : null,
        trainerIds: Array.isArray(values.trainers) && values.trainers.length ? values.trainers : null,
        ticketType: selectedTicket?.ticketType || 'COURSE_TICKET', // 일일권 여부 저장
        isTrialTicket: isTrialTicket,
        usedPoint: resolveUsedPoint(values.usedPoint),
      };
    } else if (activeTab === 'pass') {
      const selectedPass = coursePass?.find(p => p.value === values.coursePass);
      cartItem = {
        ...cartItem,
        productId: values.coursePass,
        name: selectedPass?.label || '',
        price: ticketPassPrice,
        basePrice: passBasePrice,
        discountAmount: ticketPassDiscountAmount,
        outstandingPrice: ticketPassOutstandingPrice,
        finalPrice: ticketPassFinalPrice,
        sourceType: selectedPass?.sourceType, // 'COURSE_PASS' | 'TRIAL_TICKET' — bulk 전송 시 courseType 분기용
        usedPoint: resolveUsedPoint(values.usedPoint),
      };
    } else if (activeTab === 'product') {
      cartItem = {
        ...cartItem,
        type: productType === 'LOCKER' ? 'locker' : 'product',
        productId: values.productId,
        name: values.productName || '',
        price: productPrice * (productQuantity || 1),
        basePrice: productBasePrice * (productQuantity || 1),
        discountAmount: productDiscountAmount,
        outstandingPrice: productOutstandingPrice,
        finalPrice: productFinalPrice,
        quantity: productQuantity || 1,
        usedPoint: resolveUsedPoint(values.usedPoint),
        ...(productType === 'LOCKER' && {
          lockerId: values.lockerId,
          lockerInfo: values.lockerInfo || '',
        }),
      };
    }

    // 카트에 추가
    setCartItems(prev => [...prev, cartItem]);
    toast.success(t('상품이 담겼습니다'));

    // 사용한 포인트 필드 초기화 (탭 공통)
    setValue('usedPoint', '');

    // 폼 필드 초기화 (다음 상품 선택을 위해)
    if (activeTab === 'ticket') {
      setValue('courseTicket', null);
      setValue('trainers', []);
      setUsageCount([]);
      setTicketPrice([]);
      setTicketDiscountAmount('');
      setTicketFinalPrice('');
      setTicketOutstandingPrice('');
      setSelectedTicketType(null);
      setTicketUsagePeriod(null);
      setTicketUsagePeriodType(null);
    } else if (activeTab === 'pass') {
      setValue('coursePass', null);
      setTicketPassPrice(null);
      setTicketPassDiscountAmount('');
      setTicketPassFinalPrice('');
      setTicketPassOutstandingPrice(null);
      setPassUsagePeriod(null);
      setPassUsagePeriodType(null);
    } else if (activeTab === 'product') {
      resetProductFields();
      setValue('lockerId', null);
      setValue('lockerInfo', '');
    }

    // 날짜는 유지 (이용 시작일은 공통으로 사용될 수 있음)
  };

  const handleRemoveFromCart = index => {
    setCartItems(prev => prev.filter((_, i) => i !== index));
    toast.success(t('상품이 삭제되었습니다'));
  };

  // ============================================
  // React Query - Mutations
  // ============================================
  // 판매 성공 시 포인트 사용/자동 적립이 발생하므로 보유 포인트·내역 쿼리를 모두 갱신
  const invalidatePointQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['memberPointBalance'] });
    queryClient.invalidateQueries({ queryKey: ['memberPoints'] });
    queryClient.invalidateQueries({ queryKey: ['branchPointHistories'] });
  };

  const createProductMutation = useMutation({
    mutationFn: request => createProductList(request, user.token),
    onSuccess: async () => {
      if (userInfo.userType === 'member') {
        await fetchData();
      }
      // 락커 목록 갱신
      queryClient.invalidateQueries({ queryKey: ['lockers', user.branch?.branchId] });
      // 일반상품 목록 갱신 (GeneralProductDetail)
      queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
      invalidatePointQueries();
      toast.success('등록되었습니다');
      onSaved?.();
      handleClose();
      if (userInfo.userType === 'temporary' && redirectOnTemporarySuccess) {
        setTimeout(() => {
          window.location.href = '/temporary';
        }, 500);
      }
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  const createProductGroupMutation = useMutation({
    mutationFn: request => createProductListGroup(request, user.token),
    onSuccess: async () => {
      if (userInfo.userType === 'member') {
        await fetchData();
      }
      // 락커 목록 갱신
      queryClient.invalidateQueries({ queryKey: ['lockers', user.branch?.branchId] });
      // 일반상품 목록 갱신 (GeneralProductDetail)
      queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
      invalidatePointQueries();
      toast.success('등록되었습니다');
      onSaved?.();
      handleClose();
      if (userInfo.userType === 'temporary' && redirectOnTemporarySuccess) {
        setTimeout(() => {
          window.location.href = '/temporary';
        }, 500);
      }
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // Bulk payment mutation - 여러 상품을 한 번에 결제 등록
  const createProductBulkMutation = useMutation({
    mutationFn: request => createProductBulk(request, user.token),
    onSuccess: async () => {
      if (userInfo.userType === 'member') {
        await fetchData();
      }
      // 락커 목록 갱신
      queryClient.invalidateQueries({ queryKey: ['lockers', user.branch?.branchId] });
      // 일반상품 목록 갱신 (GeneralProductDetail)
      queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
      invalidatePointQueries();
      toast.success('등록되었습니다');
      onSaved?.();
      setPaymentModalOpen(false); // 결제 모달 닫기
      handleClose();
      if (userInfo.userType === 'temporary' && redirectOnTemporarySuccess) {
        setTimeout(() => {
          window.location.href = '/temporary';
        }, 500);
      }
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 중복체크 검색 — 저장 직전, 임시회원의 휴대폰 번호로 기존 회원 매칭 조회.
  // (onSuccess 에서 참조하는 runSubmitFlow 는 아래에 선언되지만, 클로저는 호출 시점에
  //  해당 함수를 lexical scope 에서 찾으므로 정의 순서는 문제 되지 않는다.)
  const dupCheckSearchMutation = useMutation({
    // 진입 화면(상담 상세 등)이 연동 계정을 안 넘겨도, 앱 연동된 잠재회원이면 그 계정으로 판매되니 안내하지 않는다(2026-10-22)
    mutationFn: async phoneNumber => {
      const detail = await fetchTemporaryUserDetail(userInfo.userId);
      if (detail?.linkedUserId) return [];
      // 판매하는 지점 회원만 안내한다 — 다른 지점(센터)에서 쓰던 회원은 무시하고, 필요하면 앱에서 센터 연동으로 합친다(2026-10-28)
      return searchUserByPhoneNumber(phoneNumber, user.branch?.branchId, true);
    },
    onSuccess: results => {
      if (results && results.length > 0) {
        setDupCheckResults(results);
        setDupCheckModalOpen(true);
        // 모달에서 안내를 확인하고 [저장]을 누르면 runSubmitFlow(true) 가 호출되어 본 저장 흐름으로 진입.
      } else {
        // 매칭 결과가 없으면 그대로 저장 흐름 재개
        runSubmitFlow(true);
      }
    },
  });

  const isSubmitting =
    createProductMutation.isPending ||
    createProductGroupMutation.isPending ||
    createProductBulkMutation.isPending ||
    dupCheckSearchMutation.isPending;

  // ============================================
  // Form Validation
  // ============================================
  const validateForm = values => {
    const errors = {};

    // 카트 결제 — 항목별 검증은 담기 시점에 끝났고 현재 탭 입력은 비어 있는 게 정상이므로
    // 상품/기간/포인트 검사를 건너뛰고 판매 담당자만 확인한다.
    if (cartItems.length > 0) {
      if (!values.branchManager?.length) {
        setErrorFields({ branchManager: true });
        toast.error('판매 담당자를 선택해주세요');
        return false;
      }
      setErrorFields({});
      return true;
    }

    // 탭별 필수 필드 검사
    if (activeTab === 'ticket' && !values.courseTicket) {
      errors.courseTicket = true;
    }
    if (activeTab === 'pass' && !values.coursePass) {
      errors.coursePass = true;
    }
    if (activeTab === 'product' && !values.productId) {
      errors.productId = true;
    }
    // 락커 상품의 lockerId 는 옵션 — 미선택이면 미배정 패스로 판매된다.

    // 공통 필수 필드
    // 이용 기간은 수강권, 이용권, 기간 상품, 라커에 필수
    if (
      activeTab === 'ticket' ||
      activeTab === 'pass' ||
      (activeTab === 'product' && (productType === 'LOCKER' || productType === 'PERIOD'))
    ) {
      if (!values.courseStartDate) {
        errors.courseStartDate = true;
      }
    }
    if (!values.branchManager?.length || !isSellerShareValid(values.branchManager, values.sellerShares)) {
      errors.branchManager = true;
    }

    // 포인트 사용 유효성 검사 (듀엣레슨은 그룹결제라 이번 범위에서 포인트 사용 제외) — 메시지가 동적이라 별도 관리
    let usedPointErrorMessage = null;
    const isPointFieldVisible = isPointFieldVisibleNow();
    if (isPointFieldVisible) {
      const activePaymentAmount =
        activeTab === 'ticket' ? ticketFinalPrice : activeTab === 'pass' ? ticketPassFinalPrice : productFinalPrice;
      usedPointErrorMessage = pointUsage.validateUsedPoint(values.usedPoint, activePaymentAmount);
      if (usedPointErrorMessage) {
        errors.usedPoint = true;
      }
    }

    // 할인+미수금이 판매금액을 넘으면 결제금액이 음수가 된다. 백엔드에 금액 검증이 없어
    // 그대로 저장되므로 여기서 막는다(판매금액 편집·할인 입력 양쪽 경로를 함께 커버).
    const activeFinalPrice =
      activeTab === 'ticket' ? ticketFinalPrice : activeTab === 'pass' ? ticketPassFinalPrice : productFinalPrice;
    if (num(activeFinalPrice) < 0) {
      errors.negativeFinalPrice = true;
    }

    setErrorFields(errors);

    if (Object.keys(errors).length > 0) {
      const errorMessages = {
        negativeFinalPrice: '할인금액과 미수금의 합이 판매금액보다 큽니다. 금액을 확인해주세요',
        courseTicket: '수강권을 선택해주세요',
        coursePass: '이용권을 선택해주세요',
        productId: '상품을 선택해주세요',
        lockerId: '라커를 선택해주세요',
        courseStartDate: '이용 기간을 입력해주세요',
        branchManager: values.branchManager?.length ? '판매 담당자 귀속 비율의 합은 100% 여야 합니다' : '판매 담당자를 선택해주세요',
        usedPoint: usedPointErrorMessage,
      };
      const firstError = Object.keys(errors)[0];
      toast.error(errorMessages[firstError]);
      return false;
    }
    return true;
  };

  // 실제 저장 흐름 (중복체크 게이트 통과 후 진입). skipDupCheck=true 면 검색 단계를 건너뜀.
  const runSubmitFlow = (skipDupCheck = false) => {
    // 중복확인 조회 중에는 다시 들어오지 않는다(조회 성공 후 이어가는 호출은 skipDupCheck=true 라 통과).
    if (!skipDupCheck && dupCheckSearchMutation.isPending) return;
    // 1) 임시회원이고 휴대폰 번호가 있으면 — 매 저장 시 중복체크부터 수행
    // 앱 계정과 연동된 잠재회원은 같은 번호의 상대가 그 계정 본인이라 중복확인을 건너뛴다.
    const alreadyLinked = !!userInfo?.linkedUserId;
    const phoneForDupCheck = userDetail?.phoneNumber;
    if (
      !skipDupCheck &&
      !skipTemporaryDupCheck &&
      !alreadyLinked &&
      userInfo?.userType === 'temporary' &&
      phoneForDupCheck
    ) {
      dupCheckSearchMutation.mutate(String(phoneForDupCheck).replaceAll('-', ''));
      return;
    }

    const values = getValues();

    // 2) 결제수단은 결제 모달에서 받는다 — 카트 유무와 무관하게 항상 결제 모달을 연다.
    //    카트가 비어 있으면 현재 입력한 상품 1건을 단건 API 로 저장한다.
    if (!validateForm(values)) return;
    setPaymentModalOpen(true);
  };

  // Submit Click Handler - 사용자가 저장 버튼을 누른 진입점
  const onSubmitClick = () => runSubmitFlow(false);

  // 중복체크 모달 닫기 (닫기 버튼/ESC/백드롭) — 저장 흐름을 진입하지 않고 그대로 종료.
  const handleCloseDupCheckModal = () => {
    setDupCheckModalOpen(false);
  };

  // 같은 번호 회원은 안내만 한다(2026-10-22). 판매는 항상 새 회원으로 — 같은 사람이면 나중에 [앱 연동] 기존 계정으로 합친다.
  // 앱 연동된 잠재회원은 위에서 중복확인을 건너뛰고 그 계정으로 판매된다.
  const handleProceedAsNewMember = () => {
    setDupCheckModalOpen(false);
    runSubmitFlow(true);
  };

  // 저장 대상 중 상품 원가보다 비싼 건을 찾는다. 카트가 비어 있으면 지금 입력 중인 1건으로 판정한다.
  const findOverPricedItem = () => {
    if (cartItems.length > 0) {
      const over = cartItems.find(item => num(item.price) > num(item.basePrice) && num(item.basePrice) > 0);
      return over ? { name: over.name, price: num(over.price), basePrice: num(over.basePrice) } : null;
    }
    const price =
      activeTab === 'ticket'
        ? num(ticketPrice)
        : activeTab === 'pass'
          ? num(ticketPassPrice)
          : num(productPrice);
    const basePrice =
      activeTab === 'ticket'
        ? num(ticketBasePrice)
        : activeTab === 'pass'
          ? num(passBasePrice)
          : num(productBasePrice);
    if (basePrice <= 0 || price <= basePrice) return null;
    return { name: '', price, basePrice };
  };

  // 실제 저장. 원가 초과 확인을 통과한 뒤에만 호출된다.
  const runActualSubmit = () => {
    const values = getValues();
    if (cartItems.length > 0) {
      submitBulk(values);
      return;
    }
    submit(values);
  };

  // 결제 모달에서 확인 버튼 클릭 시 호출
  const handlePaymentConfirm = () => {
    const overPriced = findOverPricedItem();
    if (overPriced) {
      setOverPriceItem(overPriced);
      return;
    }
    runActualSubmit();
  };

  // 결제 모달의 수납 라인 → 서버 payments[]. 라인이 없으면 null(기존 단일 결제 방식).
  const buildMixedPayments = () =>
    mixedPayLines.length > 0
      ? mixedPayLines.map(line => ({
          method: line.method,
          amount: line.amount,
          ...(line.method === 'CARD' && {
            installmentMonth: line.installmentMonth || null,
            cardCompanyCode: line.cardCompanyCode || null,
            cardNumber: line.cardNumber || null,
            cid: line.cid || null,
          }),
          ...(line.method === 'TRANSFER' && {
            bankInfo: line.bankInfo || null,
            depositorName: line.depositorName || null,
          }),
          // 현금·계좌이체 승인번호는 주문 레벨 입력(현금영수증 폼)이다. 서버가 단일 라인이면 라인 cid 로
          // member_order.C_ID 를 덮어쓰므로 라인에도 실어야 남는다. 미발행이어도 기록한다(고객사 요청 2026-09-15).
          ...((line.method === 'CASH' || line.method === 'TRANSFER') && {
            cid: watch('approvalNumber') || null,
          }),
        }))
      : null;

  // Bulk Submit Handler - 카트에 담긴 여러 상품을 한 번에 결제 등록
  const submitBulk = values => {
    // 날짜 포맷 변환 (백엔드 LocalDateTime 형식: YYYY-MM-DD HH:mm:ss)
    const saleDate = values.saleDate
      ? moment(values.saleDate).format('YYYY-MM-DD HH:mm:ss')
      : moment().format('YYYY-MM-DD HH:mm:ss');

    // 분할결제(#49) — 라인은 모달 총액 기준이며 서버가 아이템별로 배분한다.
    const mixedPayments = buildMixedPayments();
    // 주문의 결제수단 컬럼은 서버가 라인에서 다시 결정한다(단일 수단이면 그 수단, 복수면 MIXED).
    // 다만 요청 필드가 필수라 첫 라인 수단을 보낸다.
    const mixedFirstMethod = mixedPayments ? SPLIT_METHOD_LABEL[mixedPayLines[0].method] : null;
    // 현금영수증은 주문 레벨 정보 — 현금·계좌이체 라인이 하나라도 있으면 함께 보낸다.
    const mixedHasCashLike = mixedPayLines.some(line => line.method === 'CASH' || line.method === 'TRANSFER');

    // 카트 아이템들을 API 형식으로 변환
    const items = cartItems.map(item => {
      const baseItem = {
        memberId: userInfo.userId,
        userId: userInfo.userId,
        userType: userInfo.userType,
        // #73 다지점 공유 수강권 — 판매지점 귀속(선택 지점, 없으면 로그인 지점)
        branchId: selectedBranch || user?.branch?.branchId,
        paymentMethod: mixedFirstMethod || values.paymentMethod,
        registeredBy: item.branchManagerId, // 각 아이템별로 담기 시 지정한 판매담당자 사용
        ...(item.branchManagerIds && { registeredByIds: item.branchManagerIds, shareRates: item.branchManagerRates }), // #122
        registerType: values.registerType || null, // #124 한 회원의 장바구니라 모달에서 고른 값을 전 상품에 적용
        memo: values.memo || '',
        paymentDate: saleDate,
        saleDate: saleDate,
        courseStartDate: item.startDate ? moment(item.startDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        courseEndDate: item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : null,
        // 이 건의 판매금액. 서버가 member_order.PAYMENT_REGULAR_AMOUNT 로 저장한다.
        ticketPrice: Number(String(item.price || 0).replace(/,/g, '')),
        ticketDiscountAmount: Number(String(item.discountAmount || 0).replace(/,/g, '')),
        // 결제금액(판매금액-할인-미수금)에서 사용 포인트를 차감한 금액이 실제 결제수단으로 청구되는 금액이다.
        ticketFinalPrice: Number(String(item.finalPrice || 0).replace(/,/g, '')) - (Number(item.usedPoint) || 0),
        ticketOutstandingPrice: Number(String(item.outstandingPrice || 0).replace(/,/g, '')),
        usedPoint: Number(item.usedPoint) || 0,
        // 카드 결제 정보
        ...(values.paymentMethod === '카드' && {
          installmentMonth: values.installmentMonth || null,
          cardCompanyCode: values.cardCompanyCode || null,
          cardNumber: values.cardNumber || null,
          cid: values.approvalNumber || null,
        }),
        // 계좌이체 정보
        ...(values.paymentMethod === '계좌이체' && {
          ...(values.receiptYn === 'Y' && {
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS',
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS',
          }),
          receiptYn: values.receiptYn || 'N',
          bankInfo: values.bankInfo || null,
          depositorName: values.depositorName || null,
        }),
        // 현금 정보
        ...(values.paymentMethod === '현금' && {
          ...(values.receiptYn === 'Y' && {
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS',
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS',
          }),
          receiptYn: values.receiptYn || 'N',
        }),
        // 분할결제 — 현금·계좌이체 라인이 있으면 현금영수증 정보를 주문 레벨로 보낸다.
        ...(mixedPayments &&
          mixedHasCashLike && {
            ...(values.receiptYn === 'Y' && {
              deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS',
              receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
              receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS',
            }),
            receiptYn: values.receiptYn || 'N',
            // 현금·계좌이체 승인번호 — 단건 경로(singleReceiptFields)와 같은 이유로 여기서 보낸다.
            cid: values.approvalNumber || null,
          }),
      };

      // 타입에 따른 추가 정보
      if (item.type === 'ticket') {
        // 일일권 여부에 따라 courseType만 다르게 설정
        return {
          ...baseItem,
          courseType: item.isTrialTicket ? 'TRIAL' : 'TICKET',
          productId: item.productId,
          usageCount: item.usageCount || 0,
          usedCount: 0,
          ...(item.trainerId && { trainerId: item.trainerId }),
          ...(item.trainerIds && item.trainerIds.length && { trainerIds: item.trainerIds }),
        };
      } else if (item.type === 'pass') {
        return {
          ...baseItem,
          courseType: item.sourceType === 'TRIAL_TICKET' ? 'TRIAL' : 'PASS',
          productId: item.productId,
        };
      } else if (item.type === 'locker') {
        return {
          ...baseItem,
          courseType: 'PRODUCT',
          productId: item.productId,
          lockerId: item.lockerId,
          usageStartDate: item.startDate ? moment(item.startDate).format('YYYY-MM-DD') : null,
          usageEndDate: item.endDate ? moment(item.endDate).format('YYYY-MM-DD') : null,
        };
      } else {
        // 일반 상품
        return {
          ...baseItem,
          courseType: 'PRODUCT',
          productId: item.productId,
          usageCount: item.quantity || 1,
        };
      }
    });

    const request = {
      memberId: userInfo.userId,
      userId: userInfo.userId,
      userType: userInfo.userType,
      // #73 다지점 공유 수강권 — 판매지점 귀속(선택 지점, 없으면 로그인 지점)
      branchId: selectedBranch || user?.branch?.branchId,
      items: items,
      ...(mixedPayments && { payments: mixedPayments }),
    };

    createProductBulkMutation.mutate(request);
  };

  // Submit Handler
  const submit = values => {
    // 날짜 포맷 변환
    if (values.courseStartDate) {
      values.courseStartDate = moment(values.courseStartDate).format('YYYY-MM-DD');
    }
    if (values.courseEndDate) {
      values.courseEndDate = moment(values.courseEndDate).format('YYYY-MM-DD');
    }
    if (values.saleDate) {
      values.saleDate = moment(values.saleDate).format('YYYY-MM-DD HH:mm:ss');
    }

    // 결제 모달에서 입력한 수납 라인. 주문의 결제수단 컬럼은 서버가 라인에서 다시 정하지만
    // 요청 필드가 필수라 첫 라인 수단을 함께 보낸다.
    const singlePayments = buildMixedPayments();
    const singleFirstMethod = singlePayments ? SPLIT_METHOD_LABEL[mixedPayLines[0].method] : null;
    // 현금영수증은 주문 레벨 정보 — 현금·계좌이체 라인이 있으면 함께 보낸다.
    const singleReceiptFields =
      singlePayments && mixedPayLines.some(line => line.method === 'CASH' || line.method === 'TRANSFER')
        ? {
            ...(values.receiptYn === 'Y' && {
              deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS',
              receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
              receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS',
            }),
            receiptYn: values.receiptYn || 'N',
            // 현금·계좌이체 승인번호(member_order.C_ID). 라인 결제 흐름에선 values.paymentMethod 가 비어
            // 아래 '현금'/'계좌이체' 블록이 안 타므로 여기서 보낸다. 서버 DTO 필드 cId 의 JSON 키는 cid.
            cid: values.approvalNumber || null,
          }
        : null;

    // Duet Lesson Submit
    if (selectedTicketType === 'DUET_LESSON') {
      if (selectedMembers.length === 0) {
        toast.error('듀엣 레슨은 두 번째 회원을 선택해주세요.');
        return;
      }

      if (splitPayment) {
        const ticketPriceNum = Number(ticketFinalPrice.toString().replace(/,/g, ''));
        const userDetailAmount = Number(
          (splitPaymentAmounts.userDetail === '' || !splitPaymentAmounts.userDetail
            ? '0'
            : splitPaymentAmounts.userDetail
          )
            .toString()
            .replace(/,/g, ''),
        );
        const selectedMemberAmount = Number(
          (splitPaymentAmounts.selectedMember === '' || !splitPaymentAmounts.selectedMember
            ? '0'
            : splitPaymentAmounts.selectedMember
          )
            .toString()
            .replace(/,/g, ''),
        );
        const totalAmount = userDetailAmount + selectedMemberAmount;

        if (totalAmount !== ticketPriceNum) {
          toast.error(
            `나눠 결제 금액의 합계(${formatInteger(totalAmount)}원)가 결제금액(${formatInteger(
              ticketPriceNum,
            )}원)과 일치하지 않습니다.`,
          );
          return;
        }

        // 미수금도 회원별 입력값의 합이 전체 미수금과 맞아야 한다
        const outstandingTotalNum = num(ticketOutstandingPrice);
        const userDetailOutstanding = num(splitOutstandingAmounts.userDetail);
        const selectedMemberOutstanding = num(splitOutstandingAmounts.selectedMember);
        const outstandingSum = userDetailOutstanding + selectedMemberOutstanding;

        if (outstandingSum !== outstandingTotalNum) {
          toast.error(
            `나눠 결제 미수금의 합계(${formatInteger(outstandingSum)}원)가 미수금(${formatInteger(
              outstandingTotalNum,
            )}원)과 일치하지 않습니다.`,
          );
          return;
        }

        // 결제금액이 0이어도 미수금을 지면 나눠결제가 성립한다 — 둘의 합으로 판단한다
        if (
          userDetailAmount + userDetailOutstanding <= 0 ||
          selectedMemberAmount + selectedMemberOutstanding <= 0
        ) {
          toast.error('나눠 결제는 회원별 결제금액과 미수금의 합이 0보다 커야 합니다.');
          return;
        }
      }

      // 회원별 금액은 프론트에서 확정해 보낸다. 서버는 배분 계산을 하지 않는다.
      // 결제금액·미수금은 입력값, 할인·정가는 (결제 + 미수) 비율로 도출한다.
      const duetTotalPaid = num(ticketFinalPrice);
      const duetTotalOutstanding = num(ticketOutstandingPrice);
      const duetTotalDiscount = num(ticketDiscountAmount);

      const payerPaid = splitPayment ? num(splitPaymentAmounts.userDetail) : duetTotalPaid;
      const partnerPaid = splitPayment ? num(splitPaymentAmounts.selectedMember) : 0;
      const payerOutstanding = splitPayment ? num(splitOutstandingAmounts.userDetail) : duetTotalOutstanding;
      const partnerOutstanding = splitPayment ? num(splitOutstandingAmounts.selectedMember) : 0;

      const payerBase = payerPaid + payerOutstanding;
      const partnerBase = partnerPaid + partnerOutstanding;
      const duetBaseSum = payerBase + partnerBase;
      // 반올림 잔액은 뒤 회원에게 몰아 합계를 정확히 맞춘다
      const payerDiscount =
        duetBaseSum > 0 ? Math.round((duetTotalDiscount * payerBase) / duetBaseSum) : duetTotalDiscount;
      const partnerDiscount = duetTotalDiscount - payerDiscount;

      const duetAmounts = (paid, outstanding, discount) => ({
        paymentAmount: paid,
        outstandingAmount: outstanding,
        discountAmount: discount,
        regularAmount: paid + outstanding + discount,
      });

      const duetPaymentFields = {
        paymentMethod: singleFirstMethod || values.paymentMethod,
        cardCompanyCode: values.paymentMethod === '카드' ? values.cardCompanyCode || null : null,
        cardCompany: null,
        cardNumber: values.paymentMethod === '카드' ? values.cardNumber || null : null,
        usageCount: values.usageCount,
      };

      const request = {
        courseType: 'TICKET',
        productId: activeTab === 'ticket' ? values.courseTicket : values.coursePass,
        // #73 다지점 공유 수강권 — 판매지점 귀속(선택 지점, 없으면 로그인 지점)
        branchId: selectedBranch || user?.branch?.branchId,
        ...buildSellerPayload(values.branchManager, values.sellerShares), // #122 registeredBy(첫 담당자)+registeredByIds+shareRates
        registerType: values.registerType || null, // #124
        saleDate: values.saleDate,
        courseStartDate: values.courseStartDate,
        memo: values.memo,
        ...(selectedTicketType === 'DUET_LESSON' && {
          trainerId: Array.isArray(values.trainers) && values.trainers.length ? values.trainers[0] : null,
          trainerIds: Array.isArray(values.trainers) && values.trainers.length ? values.trainers : null,
        }),
        totalFinalPrice: payerPaid + partnerPaid,
        totalOutstandingPrice: duetTotalOutstanding,
        totalDiscountAmount: duetTotalDiscount,
        ...(values.paymentMethod === '카드' && {
          installmentMonth: values.installmentMonth || null,
          cId: values.approvalNumber || null,
        }),
        ...(values.paymentMethod === '계좌이체' && {
          ...(values.receiptYn === 'Y' && {
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS',
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS',
          }),
          receiptYn: values.receiptYn || null,
          bankInfo: values.bankInfo || null,
          depositorName: values.depositorName || null,
          cId: values.approvalNumber || null,
        }),
        ...(values.paymentMethod === '현금' && {
          ...(values.receiptYn === 'Y' && {
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS',
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS',
          }),
          receiptYn: values.receiptYn || null,
          cId: values.approvalNumber || null,
        }),
        ...(singleReceiptFields || {}),
        members: [
          {
            memberId: userInfo.userId,
            ...duetAmounts(payerPaid, payerOutstanding, payerDiscount),
            ...duetPaymentFields,
          },
          ...(selectedMembers.length > 0
            ? [
                {
                  memberId: selectedMembers[0].id,
                  ...duetAmounts(partnerPaid, partnerOutstanding, partnerDiscount),
                  ...duetPaymentFields,
                },
              ]
            : []),
        ],
      };

      createProductGroupMutation.mutate(request);
    } else if (activeTab === 'product') {
      // Product Submit (재고상품, 기간상품, 락커)
      const request = {
        userId: userInfo.userId,
        memberId: userInfo.userId,
        userType: userInfo.userType,
        courseType: 'PRODUCT',
        productId: values.productId,
        // #73 다지점 공유 수강권 — 판매지점 귀속(선택 지점, 없으면 로그인 지점)
        branchId: selectedBranch || user?.branch?.branchId,
        paymentMethod: singleFirstMethod || values.paymentMethod,
        ...buildSellerPayload(values.branchManager, values.sellerShares), // #122 registeredBy(첫 담당자)+registeredByIds+shareRates
        registerType: values.registerType || null, // #124
        saleDate: values.saleDate,
        ...((productType === 'LOCKER' || productType === 'PERIOD') && {
          usageStartDate: values.courseStartDate,
          usageEndDate: values.courseEndDate,
        }),
        // 이 건의 판매금액(단가×수량). 서버가 member_order.PAYMENT_REGULAR_AMOUNT 로 저장한다.
        ticketPrice: calcProductTotalPrice(num(productPrice), num(productQuantity)),
        ticketDiscountAmount: Number(String(productDiscountAmount || 0).replace(/,/g, '')),
        // 결제금액(판매금액-할인-미수금)에서 사용 포인트를 차감한 금액이 실제 결제수단으로 청구되는 금액이다.
        ticketFinalPrice: Number(String(productFinalPrice || 0).replace(/,/g, '')) - resolveUsedPoint(values.usedPoint),
        ticketOutstandingPrice: Number(String(productOutstandingPrice || 0).replace(/,/g, '')),
        usedPoint: resolveUsedPoint(values.usedPoint),
        ...(productType === 'STOCK' && { usageCount: productQuantity || 1 }),
        ...(productType === 'LOCKER' && values.lockerId && { lockerId: values.lockerId }),
        memo: values.memo,
        ...(values.paymentMethod === '카드' && {
          installmentMonth: values.installmentMonth || null,
          cardCompanyCode: values.cardCompanyCode || null,
          cardNumber: values.cardNumber || null,
          cId: values.approvalNumber || null,
        }),
        ...(values.paymentMethod === '계좌이체' && {
          ...(values.receiptYn === 'Y' && {
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS' || null,
          }),
          receiptYn: values.receiptYn || null,
          bankInfo: values.bankInfo || null,
          depositorName: values.depositorName || null,
          cId: values.approvalNumber || null,
        }),
        ...(values.paymentMethod === '현금' && {
          ...(values.receiptYn === 'Y' && {
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS' || null,
          }),
          receiptYn: values.receiptYn || null,
          cId: values.approvalNumber || null,
        }),
        ...(singleReceiptFields || {}),
        ...(singlePayments && { payments: singlePayments }),
      };

      createProductMutation.mutate(request);
    } else {
      // Normal Submit (Ticket/Pass)
      // courseType 설정: 일일권이면 TRIAL, 일반 수강권이면 TICKET, 이용권이면 PASS
      let courseType = 'PASS';
      if (activeTab === 'ticket') {
        const selectedTicket = courseTicket.find(t => t.value === values.courseTicket);
        courseType = selectedTicket?.ticketType === 'TRIAL_TICKET' ? 'TRIAL' : 'TICKET';
      } else if (activeTab === 'pass') {
        const selectedPass = coursePass?.find(p => p.value === values.coursePass);
        courseType = selectedPass?.sourceType === 'TRIAL_TICKET' ? 'TRIAL' : 'PASS';
      }

      const request = {
        userId: userInfo.userId,
        memberId: userInfo.userId,
        userType: userInfo.userType,
        courseType: courseType,
        productId: activeTab === 'ticket' ? values.courseTicket : values.coursePass,
        // #73 다지점 공유 수강권 — 판매지점 귀속(선택 지점, 없으면 로그인 지점)
        branchId: selectedBranch || user?.branch?.branchId,
        paymentMethod: singleFirstMethod || values.paymentMethod,
        ...buildSellerPayload(values.branchManager, values.sellerShares), // #122 registeredBy(첫 담당자)+registeredByIds+shareRates
        registerType: values.registerType || null, // #124
        saleDate: values.saleDate,
        courseStartDate: values.courseStartDate,
        courseEndDate: values.courseEndDate,
        ...((selectedTicketType === 'PRIVATE_LESSON' || selectedTicketType === 'OT') && {
          trainerId: Array.isArray(values.trainers) && values.trainers.length ? values.trainers[0] : null,
          trainerIds: Array.isArray(values.trainers) && values.trainers.length ? values.trainers : null,
        }),
        // 이 건의 판매금액. 서버가 member_order.PAYMENT_REGULAR_AMOUNT 로 저장한다.
        ticketPrice: activeTab === 'ticket' ? num(ticketPrice) : num(ticketPassPrice),
        ...(activeTab === 'ticket'
          ? { ticketDiscountAmount: Number(values.ticketDiscountAmount.toString().replace(/,/g, '')) }
          : { ticketDiscountAmount: Number(values.ticketPassDiscountAmount.toString().replace(/,/g, '')) }),
        // 결제금액 필드는 이미 포인트가 차감된 표시값이므로 그대로 전송한다 (이중 차감 금지).
        ...(activeTab === 'ticket'
          ? { ticketFinalPrice: Number(values.ticketFinalPrice.toString().replace(/,/g, '')) }
          : {
              ticketFinalPrice: Number(values.ticketPassFinalPrice.toString().replace(/,/g, '')),
            }),
        ...(activeTab === 'ticket'
          ? { ticketOutstandingPrice: Number(values.ticketOutstandingPrice.toString().replace(/,/g, '')) }
          : { ticketOutstandingPrice: Number(values.ticketPassOutstandingPrice.toString().replace(/,/g, '')) }),
        ...(activeTab === 'ticket' ? { usageCount: values.usageCount } : { usageCount: null }),
        usedPoint: resolveUsedPoint(values.usedPoint),
        memo: values.memo,
        ...(values.paymentMethod === '카드' && {
          installmentMonth: values.installmentMonth || null,
          cardCompanyCode: values.cardCompanyCode || null,
          cardNumber: values.cardNumber || null,
          cId: values.approvalNumber || null,
        }),
        ...(values.paymentMethod === '계좌이체' && {
          ...(values.receiptYn === 'Y' && {
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS' || null,
          }),
          receiptYn: values.receiptYn || null,
          bankInfo: values.bankInfo || null,
          depositorName: values.depositorName || null,
          cId: values.approvalNumber || null,
        }),
        ...(values.paymentMethod === '현금' && {
          ...(values.receiptYn === 'Y' && {
            deductionType: values.deductionType === 0 ? 'PERSONAL' : 'BUSINESS' || null,
            receiptValue: values.deductionType === 0 ? values.issuePhoneNumber : values.issueBusinessNumber || null,
            receiptType: values.deductionType === 0 ? values.receiptType : 'BUSINESS' || null,
          }),
          receiptYn: values.receiptYn || null,
          cId: values.approvalNumber || null,
        }),
        ...(singleReceiptFields || {}),
        ...(singlePayments && { payments: singlePayments }),
      };

      createProductMutation.mutate(request);
    }
  };

  // ============================================
  // Effects
  // ============================================
  useEffect(() => {
    setValue('usageCount', usageCount);
    setValue('ticketPrice', formatInteger(ticketPrice));
    setValue('ticketFinalPrice', formatInteger(displayFinalPrice(ticketFinalPrice)));
    setValue('ticketDiscountAmount', formatInteger(ticketDiscountAmount));
    setValue('ticketOutstandingPrice', formatInteger(ticketOutstandingPrice));
  }, [
    ticketPrice,
    ticketFinalPrice,
    ticketDiscountAmount,
    usageCount,
    ticketOutstandingPrice,
    watchedUsedPoint,
  ]);

  useEffect(() => {
    setValue('passPrice', formatInteger(ticketPassPrice));
    setValue('ticketPassFinalPrice', formatInteger(displayFinalPrice(ticketPassFinalPrice)));
    setValue('ticketPassDiscountAmount', formatInteger(ticketPassDiscountAmount));
    setValue('ticketPassOutstandingPrice', formatInteger(ticketPassOutstandingPrice));
  }, [
    ticketPassPrice,
    ticketPassFinalPrice,
    ticketPassDiscountAmount,
    ticketPassOutstandingPrice,
    watchedUsedPoint,
  ]);

  useEffect(() => {
    setValue('productPrice', formatInteger(productPrice));
    setValue('productFinalPrice', formatInteger(displayFinalPrice(productFinalPrice)));
    setValue('productDiscountAmount', formatInteger(productDiscountAmount));
    setValue('productOutstandingPrice', formatInteger(productOutstandingPrice));
  }, [productPrice, productFinalPrice, productDiscountAmount, productOutstandingPrice, watchedUsedPoint]);

  // 모달 열릴 때 날짜 기본값을 오늘 날짜로 설정
  useEffect(() => {
    if (open) {
      const today = new Date();
      setValue('courseStartDate', today);
      setValue('saleDate', today);
    }
  }, [open]);

  // ============================================
  // Render - Modal Content (공통 콘텐츠)
  // ============================================
  const renderModalContent = () => (
    <>
      {/* Header */}
      <ModalHeader>
        <ModalHeaderTitle>{t('판매 등록')}</ModalHeaderTitle>
        <CloseIconWrapper onClick={requestClose}>
          <Close sx={{ width: '20px', height: '20px' }} />
        </CloseIconWrapper>
      </ModalHeader>

      {/* Content */}
      <ModalContent>
        {/* 탭 버튼 — 라커 연장으로 들어온 경우엔 라커만 팔 수 있게 탭을 숨긴다 */}
        {!initialLockerExtension && (
          <TabContainer>
            <TabButton active={activeTab === 'ticket'} position="first" onClick={() => handleTabChange('ticket')}>
              {t('수강권')}
            </TabButton>
            <TabButton active={activeTab === 'pass'} position="middle" onClick={() => handleTabChange('pass')}>
              {t('이용권')}
            </TabButton>
            {/* <TabButton active={activeTab === 'package'} position="middle" onClick={() => handleTabChange('package')}>
              {t('패키지')}
            </TabButton> */}
            <TabButton active={activeTab === 'product'} position="last" onClick={() => handleTabChange('product')}>
              {t('일반상품')}
            </TabButton>
          </TabContainer>
        )}

        {/* Ticket Section */}
        {activeTab === 'ticket' && (
          <EnterSaleInfoModalTicketSection
            control={control}
            errors={errors}
            setValue={setValue}
            watch={watch}
            branchManager={branchManager}
            courseTicket={courseTicket}
            trainers={trainers}
            selectedTicketType={selectedTicketType}
            ticketPrice={ticketPrice}
            ticketDiscountAmount={ticketDiscountAmount}
            ticketFinalPrice={ticketFinalPrice}
            ticketOutstandingPrice={ticketOutstandingPrice}
            usageCount={usageCount}
            ticketUsagePeriod={ticketUsagePeriod}
            ticketUsagePeriodType={ticketUsagePeriodType}
            selectedMembers={selectedMembers}
            setSelectedMembers={setSelectedMembers}
            splitPayment={splitPayment}
            setSplitPayment={setSplitPayment}
            splitPaymentAmounts={splitPaymentAmounts}
            setSplitPaymentAmounts={setSplitPaymentAmounts}
            splitOutstandingAmounts={splitOutstandingAmounts}
            setSplitOutstandingAmounts={setSplitOutstandingAmounts}
            userDetail={userDetail}
            searchResults={searchResults}
            setSearchResults={setSearchResults}
            setMemberId={setMemberId}
            debouncedGetMembers={debouncedGetMembers}
            handleChangeMember={handleChangeMember}
            getPrice={getPrice}
            changeTicketPrice={changeTicketPrice}
            ticketBasePrice={ticketBasePrice}
            changeTicketFinalPrice={changeTicketFinalPrice}
            changeTicketOutstandingPrice={changeTicketOutstandingPrice}
            changeTicketPaidPrice={changeTicketPaidPrice}
            formatInteger={formatInteger}
            toast={toast}
            errorFields={errorFields}
            setErrorFields={setErrorFields}
            showExpiredProducts={showExpiredProducts}
            setShowExpiredProducts={setShowExpiredProducts}
            isPointUsageEnabled={pointUsage.isPointUsageEnabled}
            availablePoint={pointUsage.availablePoint}
            minUsePoint={pointUsage.minUsePoint}
            usablePointLimit={usablePointLimitFor(ticketFinalPrice)}
          />
        )}

        {/* Pass Section */}
        {activeTab === 'pass' && (
          <EnterSaleInfoModalPassSection
            control={control}
            setValue={setValue}
            watch={watch}
            branchManager={branchManager}
            coursePass={coursePass}
            ticketPassPrice={ticketPassPrice}
            ticketPassDiscountAmount={ticketPassDiscountAmount}
            ticketPassFinalPrice={ticketPassFinalPrice}
            ticketPassOutstandingPrice={ticketPassOutstandingPrice}
            passUsagePeriod={passUsagePeriod}
            passUsagePeriodType={passUsagePeriodType}
            getPassPrice={getPassPrice}
            changeTicketPassPrice={changeTicketPassPrice}
            passBasePrice={passBasePrice}
            changeTicketPassFinalPrice={changeTicketPassFinalPrice}
            changeTicketPassOutstandingPrice={changeTicketPassOutstandingPrice}
            changeTicketPassPaidPrice={changeTicketPassPaidPrice}
            formatInteger={formatInteger}
            errorFields={errorFields}
            setErrorFields={setErrorFields}
            showExpiredProducts={showExpiredProducts}
            setShowExpiredProducts={setShowExpiredProducts}
            isPointUsageEnabled={pointUsage.isPointUsageEnabled}
            availablePoint={pointUsage.availablePoint}
            minUsePoint={pointUsage.minUsePoint}
            usablePointLimit={usablePointLimitFor(ticketPassFinalPrice)}
          />
        )}

        {/* Package Section - TODO: Implement package section */}
        {activeTab === 'package' && (
          <EnterSaleInfoModalTicketSection
            control={control}
            errors={errors}
            setValue={setValue}
            watch={watch}
            branchManager={branchManager}
            courseTicket={courseTicket}
            trainers={trainers}
            selectedTicketType={selectedTicketType}
            ticketPrice={ticketPrice}
            ticketDiscountAmount={ticketDiscountAmount}
            ticketFinalPrice={ticketFinalPrice}
            ticketOutstandingPrice={ticketOutstandingPrice}
            usageCount={usageCount}
            ticketUsagePeriod={ticketUsagePeriod}
            ticketUsagePeriodType={ticketUsagePeriodType}
            selectedMembers={selectedMembers}
            setSelectedMembers={setSelectedMembers}
            splitPayment={splitPayment}
            setSplitPayment={setSplitPayment}
            splitPaymentAmounts={splitPaymentAmounts}
            setSplitPaymentAmounts={setSplitPaymentAmounts}
            splitOutstandingAmounts={splitOutstandingAmounts}
            setSplitOutstandingAmounts={setSplitOutstandingAmounts}
            userDetail={userDetail}
            searchResults={searchResults}
            setSearchResults={setSearchResults}
            setMemberId={setMemberId}
            debouncedGetMembers={debouncedGetMembers}
            handleChangeMember={handleChangeMember}
            getPrice={getPrice}
            changeTicketPrice={changeTicketPrice}
            ticketBasePrice={ticketBasePrice}
            changeTicketFinalPrice={changeTicketFinalPrice}
            changeTicketOutstandingPrice={changeTicketOutstandingPrice}
            changeTicketPaidPrice={changeTicketPaidPrice}
            formatInteger={formatInteger}
            toast={toast}
            errorFields={errorFields}
            setErrorFields={setErrorFields}
            showExpiredProducts={showExpiredProducts}
            setShowExpiredProducts={setShowExpiredProducts}
          />
        )}

        {/* Product Section */}
        {activeTab === 'product' && (
          <EnterSaleInfoModalProductSection
            control={control}
            setValue={setValue}
            watch={watch}
            branchManager={branchManager}
            productType={productType}
            setProductType={setProductType}
            lockerOnly={Boolean(initialLockerExtension)}
            productPrice={productPrice}
            productDiscountAmount={productDiscountAmount}
            productFinalPrice={productFinalPrice}
            productOutstandingPrice={productOutstandingPrice}
            productQuantity={productQuantity}
            getProductPrice={getProductPrice}
            resetProductFields={resetProductFields}
            changeProductPrice={changeProductPrice}
            productBasePrice={productBasePrice}
            changeProductFinalPrice={changeProductFinalPrice}
            changeProductOutstandingPrice={changeProductOutstandingPrice}
            changeProductPaidPrice={changeProductPaidPrice}
            changeProductQuantity={changeProductQuantity}
            formatInteger={formatInteger}
            errorFields={errorFields}
            setErrorFields={setErrorFields}
            isPointUsageEnabled={pointUsage.isPointUsageEnabled}
            availablePoint={pointUsage.availablePoint}
            minUsePoint={pointUsage.minUsePoint}
            usablePointLimit={usablePointLimitFor(productFinalPrice)}
          />
        )}

        {/* Payment Section — 판매 담당자는 각 탭 섹션의 판매일시 옆으로 이동 */}
        <EnterSaleInfoModalPaymentSection
          control={control}
          setValue={setValue}
          watch={watch}
          activeTab={activeTab}
          productType={productType}
          errorFields={errorFields}
          setErrorFields={setErrorFields}
          branchId={selectedBranch}
          memberId={userInfo?.userId}
          userType={userInfo?.userType}
          initialLockerExtension={initialLockerExtension}
          getProductPrice={getProductPrice}
        />
      </ModalContent>

      {/* Footer */}
      <ModalFooter>
        <CancelButton onClick={requestClose}>{t('취소')}</CancelButton>
        <AddToCartButton onClick={handleAddToCart}>{t('담기')}</AddToCartButton>
        <SaveButton onClick={onSubmitClick} disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : t('다음')}
        </SaveButton>
      </ModalFooter>
    </>
  );

  // ============================================
  // Render - Main Return
  // ============================================
  return (
    <div style={{ display: 'inline-block' }}>
      {!hideButton && (
        <OpenButton onClick={handleOpen} variant="contained" sx={openButtonSx}>
          {t('판매 등록')}
        </OpenButton>
      )}

      <StyledModal
        open={open}
        onClose={requestClose}
        disableEscapeKeyDown
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        {cartItems.length > 0 ? (
          <ModalContainerWithCart>
            <ModalMainSection>{renderModalContent()}</ModalMainSection>
            <EnterSaleInfoModalCartSection
              cartItems={cartItems}
              onRemoveItem={handleRemoveFromCart}
              formatInteger={formatInteger}
              isPointUsageEnabled={pointUsage.isPointUsageEnabled}
              cartEarnPoint={pointUsage.cartEarnPoint}
            />
          </ModalContainerWithCart>
        ) : (
          <ModalContainer>{renderModalContent()}</ModalContainer>
        )}
      </StyledModal>

      {/* 결제 정보 모달 */}
      <PaymentConfirmModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onConfirm={handlePaymentConfirm}
        control={control}
        setValue={setValue}
        watch={watch}
        errorFields={errorFields}
        setErrorFields={setErrorFields}
        cartSummary={cartItems.length > 0 ? cartSummary : singleSummary}
        formatInteger={formatInteger}
        isSubmitting={isSubmitting}
        splitLines={mixedPayLines}
        setSplitLines={setMixedPayLines}
      />

      {/* 판매등록 중단 확인 — 잠재회원 등록/상담에서 바로 넘어온 경우에만 노출 */}
      <ConfirmModal
        open={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        onConfirm={() => {
          setCloseConfirmOpen(false);
          handleClose();
        }}
        message={closeConfirmMessage}
        cancelText="계속 작성"
        confirmText="종료"
        confirmColor="#4AB3BC"
      />

      {/* 상품 원가 초과 판매 확인 — 금액 오타로 정가의 몇 배가 등록되는 것을 막는다 */}
      <ConfirmModal
        open={!!overPriceItem}
        onClose={() => setOverPriceItem(null)}
        onConfirm={() => {
          setOverPriceItem(null);
          runActualSubmit();
        }}
        message={
          overPriceItem
            ? `${overPriceItem.name ? `${overPriceItem.name} — ` : ''}상품 원가 ${formatInteger(
                overPriceItem.basePrice,
              )}원보다 비싼 ${formatInteger(overPriceItem.price)}원으로 판매합니다.\n이대로 등록할까요?`
            : ''
        }
        highlightText={overPriceItem ? `${formatInteger(overPriceItem.price)}원` : ''}
        highlightColor="#4AB3BC"
        cancelText="취소"
        confirmText="등록"
        confirmColor="#4AB3BC"
      />

      {/* 중복체크 결과 모달 — 임시회원 저장 직전 휴대폰 번호 매칭 시 노출.
          이름·번호·생일만 보여주던 테이블로는 "타 지점의 그 회원"인지 판단할 수 없어 카드로 바꿨다.
          지점 칩과 보유 수강권이 이 모달의 존재 이유다. */}
      <Modal open={dupCheckModalOpen} onClose={handleCloseDupCheckModal} aria-labelledby="dup-check-modal">
        <SearchModalContainer>
          <SearchModalTitle variant="h6">{t('같은 번호로 등록된 회원')}</SearchModalTitle>
          <SearchModalSubtitle>
            {t('같은 번호로 등록된 회원이 있습니다. 그래도 이 번호로 회원을 저장하시겠습니까?')}
          </SearchModalSubtitle>
          <DupUserList>
            {dupCheckResults.map((searchUser, index) => (
              <DupUserCard
                key={searchUser.userId ?? index}
                sx={{ cursor: 'default', '&:hover': { borderColor: '#e5e7eb', backgroundColor: 'transparent', boxShadow: 'none' } }}>
                <DupUserCardHeader>
                  <Box>
                    <DupUserName>{searchUser.name || '-'}</DupUserName>
                    <DupUserMeta>
                      {[searchUser.phoneNumber, searchUser.birthDate].filter(Boolean).join(' · ') || '-'}
                    </DupUserMeta>
                  </Box>
                  <DupStatusBadge status={searchUser.memberStatus}>
                    {t(dupStatusLabel(searchUser.memberStatus))}
                  </DupStatusBadge>
                </DupUserCardHeader>

                <DupCardDivider />

                {(searchUser.branches?.length ?? 0) > 0 && (
                  <DupBranchChipRow>
                    {searchUser.branches.map(branch => (
                      <DupBranchChip key={branch.branchId} current={branch.branchId === user.branch?.branchId ? 1 : 0}>
                        {branch.branchName}
                      </DupBranchChip>
                    ))}
                  </DupBranchChipRow>
                )}

                {searchUser.courseTickets?.map(ticket => (
                  <DupAssetRow key={`t-${ticket.id}`}>
                    <DupAssetBranch title={ticket.branchName || ''}>{ticket.branchName || '-'}</DupAssetBranch>
                    <DupAssetName title={ticket.courseTicketName || ''}>{ticket.courseTicketName || t('수강권')}</DupAssetName>
                    <DupAssetCount>{formatTicketCount(ticket)}</DupAssetCount>
                    <DupAssetDate>{formatUntil(ticket.expireDate)}</DupAssetDate>
                  </DupAssetRow>
                ))}

                {searchUser.coursePasses?.map(coursePass => (
                  <DupAssetRow key={`p-${coursePass.id}`}>
                    <DupAssetBranch title={coursePass.branchName || ''}>{coursePass.branchName || '-'}</DupAssetBranch>
                    <DupAssetName title={coursePass.coursePassName || ''}>{coursePass.coursePassName || t('이용권')}</DupAssetName>
                    <DupAssetCount />
                    <DupAssetDate>{formatUntil(coursePass.expireDate)}</DupAssetDate>
                  </DupAssetRow>
                ))}

                {searchUser.expiredSummaries?.map(summary => (
                  <DupExpiredNote key={`e-${summary.branchId}`}>
                    {`${summary.branchName || '-'} · ${formatExpiredSummary(summary, t)}`}
                  </DupExpiredNote>
                ))}

                {(searchUser.branches?.length ?? 0) === 0 && (
                  <DupEmptyNote>{t('보유 중인 수강권·이용권이 없습니다.')}</DupEmptyNote>
                )}
              </DupUserCard>
            ))}
          </DupUserList>
          <SearchModalFooter>
            <Button variant="outlined" onClick={handleCloseDupCheckModal}>
              {t('닫기')}
            </Button>
            <Button variant="contained" onClick={handleProceedAsNewMember}>
              {t('저장')}
            </Button>
          </SearchModalFooter>
        </SearchModalContainer>
      </Modal>
    </div>
  );
};

export default EnterSaleInfoModal;
